import {CopilotAppIcon} from '@github-ui/copilot-avatar/CopilotAppIcon'
import {sendEvent} from '@github-ui/hydro-analytics'
import {IpmErrorBoundary, reportIpmAction, reportIpmVisible, useIpmRenderTiming} from '@github-ui/ipm-telemetry'
import type {IpmMetricTags} from '@github-ui/ipm-telemetry'
import type {Icon} from '@primer/octicons-react'
import {CopilotIcon, MarkGithubIcon} from '@primer/octicons-react'
import {Heading, Label, LinkButton} from '@primer/react'
import {Profiler, useEffect, useMemo} from 'react'

import styles from './MergeBoxNudgeSection.module.css'
import type {MergeBoxNudge, MergeBoxNudgeIcon} from '../../page-data/payloads/merge-box'

export interface MergeBoxNudgeSectionProps {
  nudge: MergeBoxNudge
}

// An icon cannot cross the payload boundary as a component, so dotcom names one instead.
const CTA_ICONS: Record<MergeBoxNudgeIcon, Icon> = {
  'copilot-app': CopilotAppIcon,
  'mark-github': MarkGithubIcon,
  copilot: CopilotIcon,
}

function analyticsEvent(nudge: MergeBoxNudge, action: string) {
  return {
    category: nudge.analyticsCategory,
    action,
    label: `location:${nudge.analyticsLocation};surface:${nudge.analyticsSurface};variant:${nudge.variant};`,
  }
}

/** The merge box's nudge slot: dotcom supplies every value, so this holds no copy, destination, or eligibility logic. */
export function MergeBoxNudgeSection({nudge}: MergeBoxNudgeSectionProps) {
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'MergeBoxNudgeSection',
      surface: nudge.analyticsSurface,
      display_location: nudge.analyticsLocation,
      category: nudge.analyticsCategory,
      nudge_id: nudge.nudgeId,
      variant: nudge.variant,
    }),
    [nudge.analyticsCategory, nudge.analyticsLocation, nudge.analyticsSurface, nudge.nudgeId, nudge.variant],
  )
  const renderTiming = useIpmRenderTiming(telemetryTags)

  // The Hydro half of the impression rides on `data-analytics-visible` below, which the
  // global instrumenter fires once the element is actually on screen.
  useEffect(() => {
    reportIpmVisible(telemetryTags)
  }, [telemetryTags])

  const CtaIcon = nudge.ctaIcon ? CTA_ICONS[nudge.ctaIcon] : undefined

  const section = (
    <div
      className={styles.Container}
      data-testid={nudge.testId}
      data-analytics-visible={JSON.stringify(analyticsEvent(nudge, 'visible'))}
    >
      <div className={styles.Layout}>
        <div className={styles.Copy}>
          <Heading as="h3" className={styles.Title}>
            {nudge.title}
            {nudge.label ? <Label variant={nudge.labelVariant ?? 'default'}>{nudge.label}</Label> : null}
          </Heading>
          <p className={styles.Description}>{nudge.description}</p>
        </div>
        <LinkButton
          href={nudge.ctaUrl}
          leadingVisual={CtaIcon}
          className={styles.Cta}
          data-testid={`${nudge.testId}-cta`}
          onClick={() => {
            sendEvent('analytics.click', analyticsEvent(nudge, nudge.ctaAnalyticsAction))
            reportIpmAction('cta_click', telemetryTags)
          }}
        >
          {nudge.ctaLabel}
        </LinkButton>
      </div>
    </div>
  )

  return (
    <IpmErrorBoundary tags={telemetryTags} boundaryName="MergeBoxNudgeSection" fallback={null}>
      {renderTiming.isEnabled ? (
        <Profiler id="MergeBoxNudgeSection" onRender={renderTiming.onRender}>
          {section}
        </Profiler>
      ) : (
        section
      )}
    </IpmErrorBoundary>
  )
}
